import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Layers3, Sparkles, TrendingUp, ExternalLink } from "lucide-react";
import DrillholeViewer3D from "@/components/DrillholeViewer3D";
import { Switch } from "@/components/ui/switch";
import { MINEPLAN } from "@/constants/testIds";
import { toast } from "sonner";
import { getCurrency } from "@/lib/currencies";
import CurrencyAmountInput from "@/components/CurrencyAmountInput";
import { loadEconomics, saveEconomics, DEFAULT_ECONOMICS, calcCutoffGrade } from "@/lib/project-economics";

export default function MinePlanTab({ projectId, onChange, currency = "USD" }) {
  // Currency symbol (e.g. "$", "₹", "€") for every "$" that used to be
  // hard-coded in this tab. Falls back to $ if the code is unknown.
  const cur = getCurrency(currency);
  const sym = cur.symbol || "$";
  const fmtM = (v) => `${sym}${((Number(v) || 0) / 1e6).toFixed(1)}M`;

  // Project-level economic defaults (persisted per project via localStorage)
  const [econ, setEcon] = useState(() => loadEconomics(projectId));
  useEffect(() => { setEcon(loadEconomics(projectId)); }, [projectId]);

  // Autosave economics on change (debounced by React state batching)
  const patchEcon = useCallback((patch) => {
    setEcon((prev) => {
      const next = { ...prev, ...patch };
      saveEconomics(projectId, next);
      return next;
    });
  }, [projectId]);
  const patchInputCurrency = useCallback((field, curCode) => {
    setEcon((prev) => {
      const next = {
        ...prev,
        input_currencies: { ...prev.input_currencies, [field]: curCode === currency ? null : curCode },
      };
      saveEconomics(projectId, next);
      return next;
    });
  }, [projectId, currency]);
  const inputCurOf = (field) => econ.input_currencies?.[field] || currency;

  const [plans, setPlans] = useState([]);
  const [holes, setHoles] = useState([]);
  const [bms, setBms] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [lgOpen, setLgOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter I-38 · P11 — hide the grey extrapolation layer for clean investor
  // screenshots. Toggle lives inside the Pit KPI card.
  const [showExtrapolation, setShowExtrapolation] = useState(true);
  // Iter I-38 · P12 — swap the 3D block colour ramp between grade and
  // classification (Measured / Indicated / Inferred). Legend renders
  // automatically inside the viewer when set to "classification".
  const [colorMode, setColorMode] = useState("grade");
  const [form, setForm] = useState({
    name: "Pit v1",
    pit_center: [0, 0, 105],
    pit_radius_top: 180,
    pit_radius_bottom: 60,
    pit_depth: 120,
    schedule_periods: 5,
  });
  const [lg, setLg] = useState({ name: "LG optimal pit", schedule_periods: 5 });

  const load = () => {
    api.get(`/projects/${projectId}/mine-plans`).then((r) => {
      setPlans(r.data);
      if (r.data.length && !active) setActive(r.data[0]);
      if (!r.data.length) setActive(null);
    });
    api.get(`/projects/${projectId}/drillholes`).then((r) => setHoles(r.data));
    api.get(`/projects/${projectId}/block-models`).then((r) => setBms(r.data));
  };

  useEffect(() => { load(); }, [projectId]);

  // Hydrate the first block model's `blocks` array from the streaming
  // endpoint so the 3D viewer can render ore blocks alongside the pit shell.
  useEffect(() => {
    const bm = bms[0];
    if (!bm?.id || bm.blocks?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/projects/${projectId}/block-models/${bm.id}/blocks?format=json&limit=200000&chunk_size=10000`
        );
        if (!cancelled && Array.isArray(data)) {
          setBms((prev) => {
            if (!prev.length || prev[0].id !== bm.id) return prev;
            const next = [...prev];
            next[0] = { ...next[0], blocks: data };
            return next;
          });
        }
      } catch (err) {
        // Non-fatal — pit shell still renders without hydrated blocks
        console.debug("[mine-plan] block hydration skipped:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [bms.length && bms[0]?.id, projectId]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/mine-plans`, form);
      toast.success("Mine plan generated");
      setOpen(false);
      setActive(data);
      load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const optimize = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Merge project-level economic assumptions with per-run overrides.
      // Backend receives numeric values already converted to project currency.
      const payload = {
        ...lg,
        slope_deg: econ.slope_deg,
        revenue_per_grade_unit: econ.revenue_per_grade_unit,
        mining_cost: econ.mining_cost,
        processing_cost: econ.processing_cost,
        ga_cost: econ.ga_cost,
        recovery_pct: econ.recovery_pct,
        payable_pct: econ.payable_pct,
        selling_cost: econ.selling_cost,
        royalty_pct: econ.royalty_pct,
      };
      const { data } = await api.post(`/projects/${projectId}/mine-plans/optimize`, payload);
      const lgs = data.lg_summary || data.pit_shell?.lg_summary || {};
      toast.success(`LG pit optimized · ${fmtM(lgs.total_value)} NPV`);
      setLgOpen(false);
      setActive(data);
      load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Optimize failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this mine plan?")) return;
    await api.delete(`/projects/${projectId}/mine-plans/${id}`);
    if (active?.id === id) setActive(null);
    load();
    onChange?.();
  };

  const maxTonnage = active?.phases?.reduce((m, p) => Math.max(m, p.tonnage), 0) || 1;

  return (
    <div className="h-[calc(100vh-8.75rem)] grid grid-cols-1 lg:grid-cols-[320px_1fr]">
      <aside className="border-r border-border bg-card p-5 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="smin-label">Mine plans</div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm"
                data-testid={MINEPLAN.createButton}
                className="rounded-none h-7 font-mono text-[10px] uppercase tracking-widest">
                <Plus className="h-3 w-3 mr-1" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-border bg-card sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Design pit & schedule</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Design a conceptual pit shell and auto-schedule phases sorted by highest grade.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="smin-label">Plan name</Label>
                  <Input required value={form.name} data-testid={MINEPLAN.nameInput}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none bg-input font-mono" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="smin-label">Radius top (m)</Label>
                    <Input type="number" value={form.pit_radius_top} data-testid={MINEPLAN.radiusTopInput}
                      onChange={(e) => setForm({ ...form, pit_radius_top: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                  </div>
                  <div>
                    <Label className="smin-label">Radius bottom (m)</Label>
                    <Input type="number" value={form.pit_radius_bottom} data-testid={MINEPLAN.radiusBottomInput}
                      onChange={(e) => setForm({ ...form, pit_radius_bottom: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                  </div>
                  <div>
                    <Label className="smin-label">Depth (m)</Label>
                    <Input type="number" value={form.pit_depth} data-testid={MINEPLAN.depthInput}
                      onChange={(e) => setForm({ ...form, pit_depth: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                  </div>
                  <div>
                    <Label className="smin-label">Periods</Label>
                    <Input type="number" value={form.schedule_periods} data-testid={MINEPLAN.periodsInput}
                      onChange={(e) => setForm({ ...form, schedule_periods: parseInt(e.target.value) })}
                      className="rounded-none bg-input font-mono" />
                  </div>
                </div>
                <Button type="submit" disabled={busy} data-testid={MINEPLAN.submitButton}
                  className="rounded-none w-full font-heading">
                  {busy ? "Generating…" : "Generate plan"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={lgOpen} onOpenChange={setLgOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline"
                data-testid="mineplan-lg-button"
                className="rounded-none h-7 font-mono text-[10px] uppercase tracking-widest border-border ml-1">
                <Sparkles className="h-3 w-3 mr-1" /> LG Optimize
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-border bg-card sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">Lerchs–Grossmann pit optimization</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Selects the economically optimal set of blocks under a fixed slope constraint using floating cones.
                  Requires an existing block model.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between gap-2 -mt-1 mb-2 pb-2 border-b border-border font-mono text-[10px] uppercase tracking-widest">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  Base currency: <span className="text-foreground font-semibold">{cur.code}</span>
                  <span className="opacity-70">({sym})</span>
                </span>
                <span className="text-muted-foreground">
                  Cutoff ≈ <span className="text-foreground">{(() => {
                    const co = calcCutoffGrade(econ);
                    return co != null ? co.toFixed(2) : "—";
                  })()}</span>
                </span>
              </div>
              <form onSubmit={optimize} className="space-y-3">
                <div>
                  <Label className="smin-label">Plan name</Label>
                  <Input required value={lg.name} data-testid="mineplan-lg-name-input"
                    onChange={(e) => setLg({ ...lg, name: e.target.value })}
                    className="rounded-none bg-input font-mono" />
                </div>

                {/* Row 1 — plain numeric (non-currency) fields */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="smin-label">Slope (°)</Label>
                    <Input type="number" step="0.5" min="0" max="90"
                      value={econ.slope_deg}
                      data-testid="mineplan-lg-slope_deg-input"
                      onChange={(e) => patchEcon({ slope_deg: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono h-9" />
                  </div>
                  <div>
                    <Label className="smin-label">Periods</Label>
                    <Input type="number" step="1" min="1" max="30"
                      value={lg.schedule_periods}
                      data-testid="mineplan-lg-schedule_periods-input"
                      onChange={(e) => setLg({ ...lg, schedule_periods: parseInt(e.target.value, 10) })}
                      className="rounded-none bg-input font-mono h-9" />
                  </div>
                  <div>
                    <Label className="smin-label">Recovery (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100"
                      value={econ.recovery_pct}
                      data-testid="mineplan-lg-recovery_pct-input"
                      onChange={(e) => patchEcon({ recovery_pct: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono h-9" />
                  </div>
                  <div>
                    <Label className="smin-label">Payable (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100"
                      value={econ.payable_pct}
                      data-testid="mineplan-lg-payable_pct-input"
                      onChange={(e) => patchEcon({ payable_pct: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono h-9" />
                  </div>
                  <div>
                    <Label className="smin-label">Royalty (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100"
                      value={econ.royalty_pct}
                      data-testid="mineplan-lg-royalty_pct-input"
                      onChange={(e) => patchEcon({ royalty_pct: parseFloat(e.target.value) })}
                      className="rounded-none bg-input font-mono h-9" />
                  </div>
                </div>

                <div className="pt-2 pb-1 smin-label border-t border-border">Monetary inputs · per-field currency</div>

                {/* Row 2 — currency-aware fields (5 fields, 2-column grid) */}
                <div className="grid grid-cols-2 gap-3">
                  <CurrencyAmountInput
                    label="Revenue /t · grade"
                    value={econ.revenue_per_grade_unit}
                    onChange={(v) => patchEcon({ revenue_per_grade_unit: v })}
                    baseCurrency={currency}
                    inputCurrency={inputCurOf("revenue_per_grade_unit")}
                    onInputCurrencyChange={(c) => patchInputCurrency("revenue_per_grade_unit", c)}
                    testId="mineplan-lg-revenue_per_grade_unit"
                  />
                  <CurrencyAmountInput
                    label="Mining cost /t"
                    value={econ.mining_cost}
                    onChange={(v) => patchEcon({ mining_cost: v })}
                    baseCurrency={currency}
                    inputCurrency={inputCurOf("mining_cost")}
                    onInputCurrencyChange={(c) => patchInputCurrency("mining_cost", c)}
                    testId="mineplan-lg-mining_cost"
                  />
                  <CurrencyAmountInput
                    label="Processing cost /t"
                    value={econ.processing_cost}
                    onChange={(v) => patchEcon({ processing_cost: v })}
                    baseCurrency={currency}
                    inputCurrency={inputCurOf("processing_cost")}
                    onInputCurrencyChange={(c) => patchInputCurrency("processing_cost", c)}
                    testId="mineplan-lg-processing_cost"
                  />
                  <CurrencyAmountInput
                    label="G&A cost /t"
                    value={econ.ga_cost}
                    onChange={(v) => patchEcon({ ga_cost: v })}
                    baseCurrency={currency}
                    inputCurrency={inputCurOf("ga_cost")}
                    onInputCurrencyChange={(c) => patchInputCurrency("ga_cost", c)}
                    testId="mineplan-lg-ga_cost"
                  />
                  <CurrencyAmountInput
                    label="Selling / TC-RC /t"
                    value={econ.selling_cost}
                    onChange={(v) => patchEcon({ selling_cost: v })}
                    baseCurrency={currency}
                    inputCurrency={inputCurOf("selling_cost")}
                    onInputCurrencyChange={(c) => patchInputCurrency("selling_cost", c)}
                    testId="mineplan-lg-selling_cost"
                  />
                </div>

                <Button type="submit" disabled={busy}
                  data-testid="mineplan-lg-submit"
                  className="rounded-none w-full font-heading mt-2">
                  {busy ? "Optimizing…" : "Run LG optimizer"}
                </Button>
                <div className="text-[10px] font-mono text-muted-foreground text-center">
                  Assumptions autosave per project · same values feed NPV schedule
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {plans.length === 0 && (
          <div className="text-sm text-muted-foreground">No mine plans yet. Generate a block model first for best results.</div>
        )}
        <div className="space-y-2">
          {plans.map((p) => (
            <div key={p.id} data-testid={MINEPLAN.card}
              onClick={() => setActive(p)}
              role="button" tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" ? setActive(p) : null)}
              className={`cursor-pointer w-full text-left border p-3 transition-colors ${
                active?.id === p.id ? "border-primary bg-accent" : "border-border hover:bg-accent/50"
              }`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-heading font-bold truncate">{p.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-1">
                    {p.phases?.length || 0} phases · pit {p.pit_shell?.depth}m
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(p.id); }}
                  data-testid={MINEPLAN.deleteButton}
                  className="p-1 hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

      </aside>

      <div className="relative min-h-[400px]">
        {active ? (
          <>
            {/* Pit-shell KPI overlay — surfaces the LG summary + drilled-envelope filter */}
            <div
              className="absolute top-3 left-3 z-20 bg-neutral-950/85 backdrop-blur border border-neutral-800 rounded-md px-3 py-2 max-w-sm space-y-1.5"
              data-testid="pit-kpi-card"
            >
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                {active.name || "Active plan"}
              </div>
              {/* Colour-mode segmented toggle — Grade vs Classification.
                  Iter I-38 · P12. */}
              <div
                className="flex items-center gap-1 pt-0.5"
                data-testid="pit-kpi-colormode-group"
              >
                {[
                  { id: "grade", label: "Grade" },
                  { id: "classification", label: "Class" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setColorMode(opt.id)}
                    data-testid={`pit-kpi-colormode-${opt.id}`}
                    className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border transition ${
                      colorMode === opt.id
                        ? "border-lime-600 text-lime-500 bg-lime-600/10"
                        : "border-neutral-800 text-neutral-500 hover:text-neutral-300 hover:border-neutral-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 font-mono text-[11px]">
                <div>
                  <div className="text-neutral-500 uppercase text-[9px] tracking-widest">Pit depth</div>
                  <div className="text-neutral-100">
                    {Number(active.pit_shell?.depth || 0).toFixed(1)} m
                  </div>
                </div>
                <div>
                  <div className="text-neutral-500 uppercase text-[9px] tracking-widest">Slope</div>
                  <div className="text-neutral-100">
                    {active.lg_summary?.slope_deg ?? "—"}°
                  </div>
                </div>
                <div>
                  <div className="text-neutral-500 uppercase text-[9px] tracking-widest">Value</div>
                  <div className="text-neutral-100">
                    {active.lg_summary?.total_value != null
                      ? `${sym}${(active.lg_summary.total_value / 1_000_000).toFixed(2)} M`
                      : "—"}
                  </div>
                </div>
              </div>
              {active.lg_summary?.search_filter ? (
                <div
                  className="flex items-center gap-2 pt-1.5 border-t border-neutral-800/60"
                  data-testid="pit-envelope-chip"
                  title="LG optimisation was constrained to the drilled envelope"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-lime-600" />
                  <span className="font-mono text-[10px] text-neutral-300">
                    Envelope · {active.lg_summary.search_filter.search_multiplier}× ·{" "}
                    {Number(active.lg_summary.search_filter.effective_radius_m).toLocaleString()} m ·{" "}
                    {(Number(active.lg_summary.search_filter.kept_ratio) * 100).toFixed(1)}% kept
                  </span>
                </div>
              ) : active.lg_summary ? (
                <div className="flex items-center gap-2 pt-1.5 border-t border-neutral-800/60"
                     data-testid="pit-envelope-chip-off">
                  <span className="inline-block h-2 w-2 rounded-full bg-neutral-600" />
                  <span className="font-mono text-[10px] text-neutral-500">
                    Envelope filter · off (whole tenement optimised)
                  </span>
                </div>
              ) : null}

              {/* Iter I-38 · P11 — investor-screenshot toggle + cross-link
                  to Stage 8 (Report) where the search multiplier lives. */}
              {active.lg_summary?.search_filter && (
                <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-neutral-800/60">
                  <label
                    className="flex items-center gap-2 cursor-pointer select-none"
                    data-testid="pit-extrap-toggle-label"
                  >
                    <Switch
                      checked={showExtrapolation}
                      onCheckedChange={setShowExtrapolation}
                      data-testid="pit-extrap-toggle"
                      className="h-4 w-7 data-[state=checked]:bg-lime-600"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                      Extrapolation
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent("smin:jump-to-stage", { detail: { stageId: 13 } })
                      )
                    }
                    data-testid="pit-kpi-goto-report"
                    className="flex items-center gap-1 font-mono text-[10px] text-lime-500 hover:text-lime-300 transition"
                    title="Change the search multiplier on Stage 8 → Report"
                  >
                    {active.lg_summary.search_filter.search_multiplier}× · Stage 8
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <DrillholeViewer3D
              drillholes={holes}
              mode="grade"
              blockModel={bms[0] || null}
              cutoff={bms[0]?.cutoff_grade || 0.3}
              pitShell={active.pit_shell}
              envelope={active.lg_summary?.search_filter}
              showExtrapolationZone={showExtrapolation}
              colorMode={colorMode}
              height="100%"
            />
          </>
        ) : (
          <div className="h-full grid place-items-center">
            <div className="text-center max-w-sm">
              <Layers3 className="h-8 w-8 text-muted-foreground mx-auto" />
              <div className="smin-label mt-3">No plan</div>
              <div className="text-sm text-muted-foreground mt-1">Design a pit shell to see it wrapped around your ore body.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
